import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useOutletContext } from "react-router-dom";

// ONLY keep the PieChart-related imports from Recharts
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

import { motion, AnimatePresence } from "framer-motion";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import DebtAnalysisTable from "../../../shared/DebtAnalysisTable";
import AssetAnalysisTable from "../../../shared/AssetAnalysisTable";
import api from "../../../../utils/apiConfig";

import ProjectionAssumptionsModal from "./ProjectionAssumptionsModal";
import { InfoCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import BalanceSheetNetWorthTrend from "./BalanceSheetNetWorthTrend";

import logger from '../../../../utils/logger';

// Colorblind-friendly palette
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

function getMonthRangeUTC(start, end) {
  const months = [];
  let date = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  const endUTC = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));

  while (date <= endUTC) {
    // Get the last day of the current month
    const lastDay = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0),
    );
    months.push(
      `${lastDay.getUTCFullYear()}-${String(lastDay.getUTCMonth() + 1).padStart(2, "0")}-${String(lastDay.getUTCDate()).padStart(2, "0")}`,
    );
    date = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1));
  }
  return months;
}

function getActiveMonthsForRecord(rec, monthsArr) {
  const start = new Date(rec.start_date || rec.created_at);
  const end = rec.end_date
    ? new Date(rec.end_date)
    : rec.updated_at
      ? new Date(rec.updated_at)
      : new Date();

  const activeMonths = [];
  let current = new Date(
    Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1),
  );
  const endUTC = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));

  while (current <= endUTC) {
    // Get the last day of the current month
    const lastDay = new Date(
      Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + 1, 0),
    );
    const key = `${lastDay.getUTCFullYear()}-${String(lastDay.getUTCMonth() + 1).padStart(2, "0")}-${String(lastDay.getUTCDate()).padStart(2, "0")}`;
    if (monthsArr.includes(key)) activeMonths.push(key);
    current = new Date(
      Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + 1, 1),
    );
  }
  return activeMonths;
}

const BalanceSheet = () => {
  const {
    householdId,
    userId,
    members = [],
    assetTypesData = [],
    taxQualificationsData = [],
    accountTypesData = [],
    financialInstitutions = [],
    ownerTypesData = [],
    onSaveDebts,
    onSaveAssets,
    onSaveProperties,
  } = useOutletContext();

  // Add debug effect to track mounting and householdId changes
  useEffect(() => {
    logger.error("BalanceSheet mounted or householdId changed:", {
      householdId,
      time: new Date().toISOString(),
      stack: new Error().stack,
    });
    return () => {
      logger.error("BalanceSheet unmounting:", {
        householdId,
        time: new Date().toISOString(),
      });
    };
  }, [householdId]);

  const [debtsData, setDebtsData] = useState([]);
  const [clientAssets, setClientAssets] = useState([]);
  const [assetsHistory, setAssetsHistory] = useState([]);
  const [debtsHistory, setDebtsHistory] = useState([]);
  const [propertiesHistory, setPropertiesHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersInHousehold, setUsersInHousehold] = useState([]);
  const [projectionWithCI, setProjectionWithCI] = useState([]);
  const [properties, setProperties] = useState([]);

  const totalLiabilities = debtsData
    .filter((item) => !item.deleted)
    .reduce((sum, item) => sum + parseFloat(item.remaining_balance || 0), 0);

  const totalAssets = useMemo(() => {
    const total =
      clientAssets
        .filter((item) => !item.deleted)
        .reduce((sum, item) => {
          const rawValue =
            item.approximate_value || item.checking_savings_balance || 0;
          const assetValue = parseFloat(rawValue);
          return sum + (isNaN(assetValue) ? 0 : assetValue);
        }, 0) + (parseFloat(properties?.[0]?.residence_value || 0) || 0);
    return total;
  }, [clientAssets, properties]);

  const netWorth = totalAssets - totalLiabilities;

  const [localAssetTypes, setLocalAssetTypes] = useState([]);
  const [localTaxQualifications, setLocalTaxQualifications] = useState([]);
  const [localAccountTypes, setLocalAccountTypes] = useState([]);
  const [localFinancialInstitutions, setLocalFinancialInstitutions] = useState(
    [],
  );
  const [localOwnerTypes, setLocalOwnerTypes] = useState([]);
  const [localMembers, setLocalMembers] = useState([]);

  // Add fetch tracking flags
  const [fetchedAssetTypes, setFetchedAssetTypes] = useState(false);
  const [fetchedTaxQualifications, setFetchedTaxQualifications] =
    useState(false);
  const [fetchedAccountTypes, setFetchedAccountTypes] = useState(false);
  const [fetchedFinancialInstitutions, setFetchedFinancialInstitutions] =
    useState(false);
  const [fetchedOwnerTypes, setFetchedOwnerTypes] = useState(false);

  const membersToUse =
    localMembers.length > 0 &&
    localMembers.some((m) => m.hasOwnProperty("isFormSubmitter"))
      ? localMembers
      : members;

  const getMemberName = (
    userIdParam,
    ownerTypeId,
    ownershipStructureId,
    secondaryOwnerId,
  ) => {
    if (
      ownershipStructureId === 2 ||
      (ownerTypeId &&
        ownerTypesToUse.find(
          (ot) =>
            ot.owner_type_id?.toString() === ownerTypeId?.toString() &&
            ot.owner_type_name.toLowerCase() === "joint",
        ))
    ) {
      return "Joint";
    }

    if (ownerTypeId) {
      const ownerType = ownerTypesToUse.find(
        (ot) => ot.owner_type_id?.toString() === ownerTypeId?.toString(),
      );
      const primaryMember =
        membersToUse.find((m) => m.isFormSubmitter) || membersToUse[0];
      const isMarried = properties?.[0]?.marital_status === "yes";

      if (ownerType) {
        const ownerTypeName = ownerType.owner_type_name.toLowerCase();

        if (!isMarried) {
          if (primaryMember) {
            const fullName = `${primaryMember.firstName || ""} ${
              primaryMember.lastName || ""
            }`.trim();
            return fullName;
          }
        }

        if (isMarried) {
          if (ownerTypeName === "joint" || ownerTypeName === "household") {
            return "Joint";
          }
          if (ownerTypeName === "self" && primaryMember) {
            const fullName = `${primaryMember.firstName || ""} ${
              primaryMember.lastName || ""
            }`.trim();
            return fullName;
          }
          if (ownerTypeName === "spouse") {
            const spouseMember = membersToUse.find((m) => !m.isFormSubmitter);
            if (spouseMember) {
              const fullName = `${spouseMember.firstName || ""} ${
                spouseMember.lastName || ""
              }`.trim();
              return fullName;
            }
          }
        }
        return ownerType.owner_type_name;
      }
    }

    if (userIdParam) {
      const userIdStr = userIdParam.toString();
      const matchedMember = membersToUse.find(
        (m) =>
          m.userId?.toString() === userIdStr ||
          m.user_id?.toString() === userIdStr,
      );
      if (matchedMember) {
        const fullName = `${matchedMember.firstName || ""} ${
          matchedMember.lastName || ""
        }`.trim();
        return fullName;
      }
    }

    const primaryMember =
      membersToUse.find((m) => m.isFormSubmitter) || membersToUse[0];
    if (primaryMember) {
      const fullName = `${primaryMember.firstName || ""} ${
        primaryMember.lastName || ""
      }`.trim();
      return fullName;
    }
    return "Primary";
  };

  const assetTypesToUse =
    assetTypesData?.length > 0 ? assetTypesData : localAssetTypes;
  const taxQualificationsToUse =
    taxQualificationsData?.length > 0
      ? taxQualificationsData
      : localTaxQualifications;
  const accountTypesToUse =
    accountTypesData?.length > 0 ? accountTypesData : localAccountTypes;
  const financialInstitutionsToUse =
    financialInstitutions?.length > 0
      ? financialInstitutions
      : localFinancialInstitutions;
  const ownerTypesToUse =
    ownerTypesData?.length > 0 ? ownerTypesData : localOwnerTypes;

  useEffect(() => {
    const fetchAssetTypes = async () => {
      if (fetchedAssetTypes || assetTypesData?.length > 0) return;
      try {
        const response = await api.get("/advisor-portal/asset-types");
        if (response.data && Array.isArray(response.data)) {
          setLocalAssetTypes(response.data);
        }
      } catch (error) {
        logger.error("Error fetching asset types:", error);
      } finally {
        setFetchedAssetTypes(true);
      }
    };
    fetchAssetTypes();
  }, [fetchedAssetTypes, assetTypesData]);

  useEffect(() => {
    const fetchTaxQualifications = async () => {
      if (fetchedTaxQualifications || taxQualificationsData?.length > 0) return;
      try {
        const response = await api.get("/advisor-portal/tax-qualifications");
        if (response.data && Array.isArray(response.data)) {
          setLocalTaxQualifications(response.data);
        }
      } catch (error) {
        logger.error("Error fetching tax qualifications:", error);
      } finally {
        setFetchedTaxQualifications(true);
      }
    };
    fetchTaxQualifications();
  }, [fetchedTaxQualifications, taxQualificationsData]);

  useEffect(() => {
    const fetchAccountTypes = async () => {
      if (fetchedAccountTypes || accountTypesData?.length > 0) return;
      try {
        const response = await api.get("/advisor-portal/account-types");
        if (response.data && Array.isArray(response.data)) {
          setLocalAccountTypes(response.data);
        }
      } catch (error) {
        logger.error("Error fetching account types:", error);
      } finally {
        setFetchedAccountTypes(true);
      }
    };
    fetchAccountTypes();
  }, [fetchedAccountTypes, accountTypesData]);

  useEffect(() => {
    const fetchFinancialInstitutions = async () => {
      if (fetchedFinancialInstitutions || financialInstitutions?.length > 0)
        return;
      try {
        const response = await api.get(
          "/advisor-portal/financial-institutions",
        );
        if (response.data && Array.isArray(response.data)) {
          setLocalFinancialInstitutions(response.data);
        }
      } catch (error) {
        logger.error("Error fetching financial institutions:", error);
      } finally {
        setFetchedFinancialInstitutions(true);
      }
    };
    fetchFinancialInstitutions();
  }, [fetchedFinancialInstitutions, financialInstitutions]);

  useEffect(() => {
    const fetchOwnerTypes = async () => {
      if (fetchedOwnerTypes || ownerTypesData?.length > 0) return;
      try {
        const response = await api.get("/advisor-portal/owner-types");
        if (response.data && Array.isArray(response.data)) {
          setLocalOwnerTypes(response.data);
        }
      } catch (error) {
        logger.error("Error fetching owner types:", error);
      } finally {
        setFetchedOwnerTypes(true);
      }
    };
    fetchOwnerTypes();
  }, [fetchedOwnerTypes, ownerTypesData]);

  useEffect(() => {
    const fetchHouseholdMembers = async () => {
      if (!householdId) return;
      setLoading(true);
      try {
        const response = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}`,
        );
        if (response.data && Array.isArray(response.data.members)) {
          setLocalMembers(response.data.members);
          const userIds = response.data.members.map((member) => member.userId);
          setUsersInHousehold(userIds);
        }
      } catch (error) {
        logger.error("Error fetching household members:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchHouseholdMembers();
  }, [householdId]);

  useEffect(() => {
    const fetchAllUserAssets = async () => {
      if (!householdId) return;
      setLoading(true);
      try {
        const response = await api.get(
          `/advisor-portal/balance-sheet/all-with-history/${householdId}`,
        );
        if (response.data && response.data.current && response.data.history) {
          setClientAssets(response.data.current.assets || []);
          setProperties(response.data.current.properties || []);
          setDebtsData(response.data.current.debts || []);
          setAssetsHistory(response.data.history.assets || []);
          setDebtsHistory(response.data.history.debts || []);
          setPropertiesHistory(response.data.history.properties || []);
        } else {
          await fetchAnnualReviewData();
        }
      } catch (error) {
        logger.error("Error fetching assets for household:", error);
        await fetchAnnualReviewData();
      } finally {
        setLoading(false);
      }
    };
    fetchAllUserAssets();
  }, [householdId]);

  const fetchAnnualReviewData = async () => {
    if (!householdId) return;
    try {
      const availableResponse = await api.get(
        `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/available`,
      );
      if (
        availableResponse.data &&
        Array.isArray(availableResponse.data) &&
        availableResponse.data.length > 0
      ) {
        const sortedReviews = [...availableResponse.data].sort((a, b) => {
          if (a.year !== b.year) return b.year - a.year;
          return b.version - a.version;
        });
        const latestReview = sortedReviews[0];
        const arResponse = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/${latestReview.year}/${latestReview.version}`,
        );
        if (arResponse.data) {
          if (arResponse.data.assets && Array.isArray(arResponse.data.assets)) {
            setClientAssets(arResponse.data.assets);
          }
          if (
            arResponse.data.properties &&
            Array.isArray(arResponse.data.properties)
          ) {
            setProperties(arResponse.data.properties);
          } else if (arResponse.data.primaryResidenceValue) {
            const defaultProperty = {
              property_id: null,
              form_id: arResponse.data.formId || null,
              own_residence: true,
              residence_value: arResponse.data.primaryResidenceValue,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            setProperties([defaultProperty]);
          }
        }
      }
    } catch (error) {
      logger.error("Error fetching annual review data:", error);
    }
  };

  const prepareAssetsData = useCallback(() => {
    const processedAssetMap = new Map();

    const regularAssets = (clientAssets || [])
      .filter((asset) => {
        if (asset.deleted) return false;
        if (!asset.start_date && !asset.end_date) return true;
        const startDate = asset.start_date ? new Date(asset.start_date) : null;
        const endDate = asset.end_date ? new Date(asset.end_date) : null;
        const now = new Date();
        return (!startDate || startDate <= now) && (!endDate || endDate >= now);
      })
      .reduce((acc, asset) => {
        const assetKey = [
          asset.financial_institution_id,
          asset.asset_type_id,
          asset.tax_qualification_id,
          asset.account_type_id,
          asset.account_number,
          asset.ownership_structure_id !== 2 ? asset.user_id : null,
        ].join("-");

        if (processedAssetMap.has(assetKey)) {
          const existingAsset = processedAssetMap.get(assetKey);
          if (new Date(asset.updated_at) > new Date(existingAsset.updated_at)) {
            acc = acc.filter((a) => a.id !== existingAsset.id);
          } else {
            return acc;
          }
        }

        const isJoint =
          asset.ownership_structure_id === 2 ||
          (asset.owner_type_id &&
            ownerTypesToUse.find(
              (ot) =>
                ot.owner_type_id?.toString() ===
                  asset.owner_type_id?.toString() &&
                ot.owner_type_name.toLowerCase() === "joint",
            ));

        let memberName;
        if (isJoint) {
          memberName = "Joint";
        } else {
          const member = membersToUse.find(
            (m) =>
              m.userId?.toString() === asset.user_id?.toString() ||
              m.user_id?.toString() === asset.user_id?.toString(),
          );
          memberName = member
            ? `${member.firstName || ""} ${member.lastName || ""}`.trim()
            : "Primary";
        }

        const balanceRaw =
          asset.approximate_value || asset.checking_savings_balance || 0;
        const cleanedValue =
          typeof balanceRaw === "string"
            ? balanceRaw.replace(/[^0-9.]/g, "")
            : balanceRaw;
        const balanceValue = parseFloat(cleanedValue);

        const assetTypeObj = assetTypesToUse.find(
          (type) =>
            type.asset_type_id?.toString() === asset.asset_type_id?.toString(),
        );
        const financialInstitutionObj = financialInstitutionsToUse.find(
          (inst) =>
            inst.institution_id?.toString() ===
            asset.financial_institution_id?.toString(),
        );
        const taxQualificationObj = taxQualificationsToUse.find(
          (tax) =>
            tax.tax_qualification_id?.toString() ===
            asset.tax_qualification_id?.toString(),
        );
        const accountTypeObj = accountTypesToUse.find(
          (acct) =>
            acct.account_type_id?.toString() ===
            asset.account_type_id?.toString(),
        );

        const processedAsset = {
          id: asset.asset_id || asset.client_asset_id,
          user_id: asset.user_id,
          memberId: asset.user_id,
          member: memberName,
          financialInstitution:
            financialInstitutionObj?.institution_name || "Unknown",
          financialInstitutionId: asset.financial_institution_id,
          assetType: asset.asset_type_id?.toString() || "",
          assetTypeName: assetTypeObj?.asset_type_name || "Unknown",
          taxQualification: asset.tax_qualification_id?.toString() || "",
          taxQualificationName:
            taxQualificationObj?.tax_qualification_name || "",
          accountType: asset.account_type_id?.toString() || "",
          accountTypeName: accountTypeObj?.account_type_name || "",
          ownerTypeId: asset.owner_type_id?.toString() || "",
          formId: asset.form_id,
          balance: isNaN(balanceValue) ? 0 : balanceValue,
          riskTolerance: asset.overall_portfolio_risk_number || "",
          lastUpdated: asset.updated_at
            ? new Date(asset.updated_at).toISOString().split("T")[0]
            : "",
          updated_at: asset.updated_at,
          created_at: asset.created_at,
          deleted: false,
          isJoint,
          account_number: asset.account_number,
        };

        processedAssetMap.set(assetKey, processedAsset);
        return [...acc, processedAsset];
      }, []);

    let primaryResidenceAsset = null;
    if (properties && properties.length > 0) {
      const currentProperty = properties[0];
      if (currentProperty && currentProperty.residence_value) {
        primaryResidenceAsset = {
          id: "primary-residence",
          isPrimaryResidence: true,
          member:
            currentProperty?.marital_status === "yes" ? "Joint" : "Primary",
          financialInstitution: "Primary Residence",
          financialInstitutionId: null,
          assetType: "residence",
          assetTypeName: "Primary Residence",
          taxQualification: "",
          taxQualificationName: "",
          accountType: "",
          accountTypeName: "",
          formId: currentProperty.form_id,
          balance: parseFloat(currentProperty.residence_value) || 0,
          riskTolerance: "",
          lastUpdated: currentProperty.updated_at
            ? new Date(currentProperty.updated_at).toISOString().split("T")[0]
            : "",
          updated_at: currentProperty.updated_at,
          created_at: currentProperty.created_at,
          deleted: false,
          isJoint: currentProperty?.marital_status === "yes",
        };
      }
    }

    const result = primaryResidenceAsset
      ? [primaryResidenceAsset, ...regularAssets]
      : regularAssets;
    return result;
  }, [
    clientAssets,
    properties,
    membersToUse,
    assetTypesToUse,
    taxQualificationsToUse,
    accountTypesToUse,
    financialInstitutionsToUse,
    ownerTypesToUse,
    getMemberName,
  ]);

  const preparedAssets = prepareAssetsData();

  const handleSaveLiabilities = (updatedDebts) => {
    if (onSaveDebts) {
      onSaveDebts(updatedDebts);
    }
  };

  const handleSaveAssets = async (updatedAssets) => {
    const primaryResidenceAsset = updatedAssets.find(
      (a) => a.isPrimaryResidence,
    );

    if (primaryResidenceAsset && properties.length > 0) {
      const updatedProperties = [...properties];
      if (primaryResidenceAsset.balance !== properties[0].residence_value) {
        updatedProperties[0] = {
          ...updatedProperties[0],
          residence_value: primaryResidenceAsset.balance,
          updated_at: new Date().toISOString(),
        };
        if (onSaveProperties) {
          onSaveProperties(updatedProperties);
        }
      }
    }

    const regularAssets = updatedAssets.filter((a) => !a.isPrimaryResidence);

    const convertedAssets = regularAssets.map((asset) => {
      const originalAsset = clientAssets.find(
        (a) =>
          a.asset_id?.toString() === asset.id?.toString() ||
          a.id?.toString() === asset.id?.toString(),
      );
      if (originalAsset) {
        return {
          ...originalAsset,
          approximate_value: asset.balance,
          financial_institution_id: asset.financialInstitutionId,
          asset_type_id: asset.assetType ? parseInt(asset.assetType, 10) : null,
          tax_qualification_id: asset.taxQualification
            ? parseInt(asset.taxQualification, 10)
            : null,
          account_type_id: asset.accountType
            ? parseInt(asset.accountType, 10)
            : null,
          overall_portfolio_risk_number: asset.riskTolerance,
          owner_type_id: asset.ownerTypeId
            ? parseInt(asset.ownerTypeId, 10)
            : null,
          updated_at: new Date().toISOString(),
        };
      } else {
        return {
          asset_id: asset.id,
          form_id: asset.formId || null,
          asset_type_id: asset.assetType ? parseInt(asset.assetType, 10) : null,
          financial_institution_id: asset.financialInstitutionId,
          owner_type_id: asset.ownerTypeId
            ? parseInt(asset.ownerTypeId, 10)
            : null,
          tax_qualification_id: asset.taxQualification
            ? parseInt(asset.taxQualification, 10)
            : null,
          account_type_id: asset.accountType
            ? parseInt(asset.accountType, 10)
            : null,
          approximate_value: asset.balance,
          overall_portfolio_risk_number: asset.riskTolerance,
          updated_at: new Date().toISOString(),
          created_at: asset.created_at || new Date().toISOString(),
          household_id: householdId,
          user_id: asset.memberId || usersInHousehold[0],
        };
      }
    });

    try {
      const response = await api.post(
        `/advisor-portal/balance-sheet/save-assets`,
        {
          assets: convertedAssets,
        },
      );
      if (response.data && response.data.success) {
        await fetchUserAssets();
      } else if (onSaveAssets) {
        onSaveAssets(convertedAssets);
      }
    } catch (error) {
      logger.error("Error saving assets:", error);
      if (onSaveAssets) {
        onSaveAssets(convertedAssets);
      }
    }
  };

  const fetchUserAssets = async () => {
    if (!householdId) return;
    setLoading(true);
    try {
      const response = await api.get(
        `/advisor-portal/balance-sheet/all-with-history/${householdId}`,
      );
      if (response.data && response.data.current && response.data.history) {
        const validAssets = (response.data.current.assets || []).filter(
          (asset) => {
            const hasValidUserId =
              asset.user_id && usersInHousehold.includes(asset.user_id);
            const hasValidHouseholdId = asset.household_id === householdId;
            const hasValidPrimaryOwner =
              asset.primary_owner_user_id &&
              usersInHousehold.includes(asset.primary_owner_user_id);
            const hasValidSecondaryOwner =
              asset.secondary_owner_user_id &&
              usersInHousehold.includes(asset.secondary_owner_user_id);
            return (
              hasValidUserId ||
              hasValidHouseholdId ||
              hasValidPrimaryOwner ||
              hasValidSecondaryOwner
            );
          },
        );

        const validHistory = (response.data.history.assets || []).filter(
          (asset) => {
            const hasValidUserId =
              asset.user_id && usersInHousehold.includes(asset.user_id);
            const hasValidHouseholdId = asset.household_id === householdId;
            const hasValidPrimaryOwner =
              asset.primary_owner_user_id &&
              usersInHousehold.includes(asset.primary_owner_user_id);
            const hasValidSecondaryOwner =
              asset.secondary_owner_user_id &&
              usersInHousehold.includes(asset.secondary_owner_user_id);
            return (
              hasValidUserId ||
              hasValidHouseholdId ||
              hasValidPrimaryOwner ||
              hasValidSecondaryOwner
            );
          },
        );

        setClientAssets(validAssets);
        setProperties(response.data.current.properties || []);
        setDebtsData(response.data.current.debts || []);
        setAssetsHistory(validHistory);
        setDebtsHistory(response.data.history.debts || []);
        setPropertiesHistory(response.data.history.properties || []);
      }
    } catch (error) {
      logger.error("Error refreshing assets:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value, useCompact = false) => {
    if (isNaN(value)) return value;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: useCompact ? "compact" : "standard",
      maximumFractionDigits: useCompact ? 1 : 2,
    }).format(value);
  };

  const formatPercentage = (value) => {
    if (!value || value === 0) return "0%";
    return `${(value * 100).toFixed(0)}%`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-surface-default dark:bg-surface-default p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const prepareAssetDistributionData = useCallback(() => {
    const distribution = {
      "Cash & Equivalents (Non-Qualified)": 0,
      "Property (Non-Qualified)": 0,
      "Tax-Deferred (Traditional)": 0,
      "Tax-Free (Roth)": 0,
    };

    clientAssets
      .filter((item) => !item.deleted)
      .forEach((asset) => {
        const value = parseFloat(asset.approximate_value || 0);
        if (isNaN(value)) return;

        const assetType = assetTypesToUse.find(
          (type) =>
            type.asset_type_id?.toString() === asset.asset_type_id?.toString(),
        );
        const taxQualification = taxQualificationsToUse.find(
          (tax) =>
            tax.tax_qualification_id?.toString() ===
            asset.tax_qualification_id?.toString(),
        );

        const assetTypeName = assetType?.asset_type_name?.toLowerCase() || "";
        const taxQualificationName =
          taxQualification?.tax_qualification_name?.toLowerCase() || "";

        if (taxQualificationName.includes("roth")) {
          distribution["Tax-Free (Roth)"] += value;
        } else if (
          taxQualificationName.includes("traditional") ||
          taxQualificationName.includes("tax-deferred")
        ) {
          distribution["Tax-Deferred (Traditional)"] += value;
        } else if (
          assetTypeName.includes("property") ||
          assetTypeName.includes("real estate")
        ) {
          distribution["Property (Non-Qualified)"] += value;
        } else {
          distribution["Cash & Equivalents (Non-Qualified)"] += value;
        }
      });

    const residenceValue =
      parseFloat(properties?.[0]?.residence_value || 0) || 0;
    if (residenceValue > 0) {
      distribution["Property (Non-Qualified)"] += residenceValue;
    }

    return Object.entries(distribution)
      .map(([name, value]) => ({ name, value }))
      .filter((item) => item.value > 0);
  }, [clientAssets, properties, assetTypesToUse, taxQualificationsToUse]);

  const assetDistributionData = prepareAssetDistributionData();

  const [netWorthTrendData, setNetWorthTrendData] = useState([]);
  const [annualNetWorthTrend, setAnnualNetWorthTrend] = useState([]);
  const [futureProjection, setFutureProjection] = useState([]);

  const getPrimaryMember = () => {
    return membersToUse.find((m) => m.isFormSubmitter) || membersToUse[0];
  };

  const getPrimaryMemberAge = () => {
    const member = getPrimaryMember();
    if (!member?.dateOfBirth) return 50;
    const dob = new Date(member.dateOfBirth);
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const m = now.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
    return age;
  };

  const getPrimaryMemberLifeExpectancy = () => {
    const member = getPrimaryMember();
    if (member?.advancedAssessment?.predicted_life_expectancy_adv) {
      return parseFloat(
        member.advancedAssessment.predicted_life_expectancy_adv,
      );
    }
    if (member?.recommendedAssessment?.predictedLifeExpectancy) {
      return parseFloat(member.recommendedAssessment.predictedLifeExpectancy);
    }
    if (member?.basicAssessment?.calculatedLifeExpectancy) {
      return parseFloat(member.basicAssessment.calculatedLifeExpectancy);
    }
    return null;
  };

  useEffect(() => {
    if (!annualNetWorthTrend.length) return;
    const now = new Date();
    const currentYear = now.getFullYear();
    const primaryAge = getPrimaryMemberAge();
    if (!primaryAge) return;
    let lifeExpectancy = getPrimaryMemberLifeExpectancy();
    if (!lifeExpectancy || isNaN(lifeExpectancy))
      lifeExpectancy = primaryAge + 25;
    const yearsToProject = Math.max(0, Math.round(lifeExpectancy - primaryAge));
    if (yearsToProject < 1) return;

    const lastActual = annualNetWorthTrend[annualNetWorthTrend.length - 1];
    let lastYear = parseInt(lastActual.date.split("-")[0]);
    let assets = lastActual.assets;
    let debts = lastActual.debts;
    let netWorth = lastActual.netWorth;

    const assetGrowth = 0.045;
    const annualDebtPayment = 30000;
    const volatility = 0.12;

    const projection = [];
    for (let i = 1; i <= yearsToProject; i++) {
      lastYear++;
      assets = assets * (1 + assetGrowth);
      debts = Math.max(0, debts - annualDebtPayment);
      netWorth = assets - debts;
      projection.push({
        date: `${lastYear}`,
        assets,
        debts,
        netWorth,
        isProjection: true,
      });
    }
    setFutureProjection(projection);
  }, [annualNetWorthTrend, membersToUse]);

  useEffect(() => {
    if (
      !assetsHistory.length &&
      !debtsHistory.length &&
      !propertiesHistory.length
    )
      return;

    // Find the earliest date from all history arrays
    const allDates = [
      ...assetsHistory.map((a) => new Date(a.start_date || a.updated_at)),
      ...debtsHistory.map((d) => new Date(d.start_date || d.updated_at)),
      ...propertiesHistory.map((p) => new Date(p.start_date || p.updated_at)),
    ].filter((d) => d && !isNaN(d));

    const now = new Date();
    const start =
      allDates.length > 0
        ? new Date(Math.min(...allDates))
        : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 35, 1));

    const monthsArr = getMonthRangeUTC(start, now);

    function buildMonthMap(historyArr, keyField) {
      const monthMap = {};
      monthsArr.forEach((m) => {
        monthMap[m] = {};
      });

      // Sort history records by start_date
      const sortedHistory = [...historyArr].sort((a, b) => {
        const aDate = new Date(a.start_date || a.updated_at);
        const bDate = new Date(b.start_date || b.updated_at);
        return aDate - bDate;
      });

      for (const rec of sortedHistory) {
        const businessKey = rec[keyField];
        const activeMonths = getActiveMonthsForRecord(rec, monthsArr);
        const recStartDate = new Date(rec.start_date || rec.updated_at);

        for (const m of activeMonths) {
          const monthDate = new Date(m);
          // Only use this record if it's the most recent one that started before this month
          if (
            !monthMap[m][businessKey] ||
            (recStartDate <= monthDate &&
              (!monthMap[m][businessKey].start_date ||
                new Date(
                  monthMap[m][businessKey].start_date ||
                    monthMap[m][businessKey].updated_at,
                ) < recStartDate))
          ) {
            monthMap[m][businessKey] = rec;
          }
        }
      }
      return monthMap;
    }

    const assetMonthMap = buildMonthMap(assetsHistory, "client_asset_id");
    const debtMonthMap = buildMonthMap(debtsHistory, "client_debt_id");
    const propertyMonthMap = buildMonthMap(
      propertiesHistory,
      "client_property_id",
    );

    const trend = monthsArr.map((m) => {
      let assetsTotal = 0;
      let debtsTotal = 0;
      let propertiesTotal = 0;

      Object.values(assetMonthMap[m]).forEach((rec) => {
        let v = null;
        if (rec.value !== undefined && rec.value !== null) {
          v = rec.value;
        } else if (
          rec.approximate_value !== undefined &&
          rec.approximate_value !== null
        ) {
          v = rec.approximate_value;
        } else if (
          rec.checking_savings_balance !== undefined &&
          rec.checking_savings_balance !== null
        ) {
          v = rec.checking_savings_balance;
        }
        if (typeof v === "string") {
          v = v.replace(/[^0-9.-]/g, "");
        }
        v = parseFloat(v || 0);
        if (!isNaN(v)) {
          assetsTotal += v;
        }
      });

      if (assetsTotal === 0 && clientAssets.length > 0) {
        assetsTotal = clientAssets.reduce((sum, asset) => {
          const value = parseFloat(
            asset.approximate_value || asset.checking_savings_balance || 0,
          );
          return sum + (isNaN(value) ? 0 : value);
        }, 0);
      }

      Object.values(debtMonthMap[m]).forEach((rec) => {
        let v = rec.remaining_balance;
        if (v === undefined || v === null) {
          v = rec.amount;
        }
        if (typeof v === "string") {
          v = v.replace(/[^0-9.-]/g, "");
        }
        v = parseFloat(v || 0);
        if (!isNaN(v)) {
          debtsTotal += v;
        }
      });

      // If we're at the current month and debtsTotal is 0, use the current total liabilities
      const currentDate = new Date();
      const monthDate = new Date(m);
      if (
        monthDate.getUTCFullYear() === currentDate.getUTCFullYear() &&
        monthDate.getUTCMonth() === currentDate.getUTCMonth() &&
        debtsTotal === 0 &&
        totalLiabilities > 0
      ) {
        debtsTotal = totalLiabilities;
      }

      Object.values(propertyMonthMap[m]).forEach((rec) => {
        let v = rec.residence_value;
        if (typeof v === "string") {
          v = v.replace(/[^0-9.-]/g, "");
        }
        v = parseFloat(v || 0);
        if (!isNaN(v)) {
          propertiesTotal += v;
        }
      });

      if (propertiesTotal === 0 && properties.length > 0) {
        propertiesTotal = parseFloat(properties[0].residence_value || 0);
      }

      const netWorth = assetsTotal + propertiesTotal - debtsTotal;

      return {
        date: m,
        assets: assetsTotal + propertiesTotal,
        debts: debtsTotal,
        netWorth,
        isZeroDebt: debtsTotal === 0,
      };
    });

    setNetWorthTrendData(trend);

    const annualMap = {};
    const lastNonZeroDebtMap = {};

    // First pass - collect all historical records
    for (let i = 0; i < trend.length; i++) {
      const year = trend[i].date.split("-")[0];
      const month = parseInt(trend[i].date.split("-")[1]);

      // For historical data, use the actual historical dates
      const recordDate = new Date(trend[i].date);
      const now = new Date();
      if (recordDate <= now) {
        if (!annualMap[year] || month === 1) {
          // Use January values for historical data
          annualMap[year] = trend[i];
        }
      } else {
        // For future dates, use December values
        if (!annualMap[year] || month === 12) {
          annualMap[year] = trend[i];
        }
      }

      if (trend[i].debts > 0 && !lastNonZeroDebtMap[year]) {
        lastNonZeroDebtMap[year] = trend[i].debts;
      }
    }

    Object.keys(annualMap).forEach((year) => {
      if (annualMap[year].debts === 0 && lastNonZeroDebtMap[year]) {
        annualMap[year] = {
          ...annualMap[year],
          debts: lastNonZeroDebtMap[year],
          isZeroDebt: false,
        };
      }
    });

    const annualTrend = Object.values(annualMap).sort((a, b) =>
      a.date.localeCompare(b.date),
    );
    setAnnualNetWorthTrend(annualTrend);
  }, [
    assetsHistory,
    debtsHistory,
    propertiesHistory,
    clientAssets,
    properties,
  ]);

  const getYear = (d) => parseInt(d.date.split("-")[0]);
  const getMonth = (d) => parseInt(d.date.split("-")[1] || "1");

  const lastHist =
    annualNetWorthTrend.length > 0
      ? annualNetWorthTrend[annualNetWorthTrend.length - 1]
      : null;
  const lastHistYear = lastHist ? getYear(lastHist) : null;
  const lastHistMonth =
    lastHist && lastHist.date.includes("-") ? getMonth(lastHist) : 12;

  const historical = annualNetWorthTrend.filter((d) => {
    const year = getYear(d);
    const month = d.date.includes("-") ? getMonth(d) : 12;
    return (
      year < lastHistYear || (year === lastHistYear && month <= lastHistMonth)
    );
  });

  logger.info("[HISTORICAL DATA]", {
    raw_history: assetsHistory.map((h) => ({
      date: h.start_date,
      value: h.value,
    })),
    processed_annual: historical.map((h) => ({
      date: h.date,
      assets: h.assets,
    })),
  });

  const projected = useMemo(() => {
    let projectedData = [];
    if (lastHist) {
      let startYear = lastHistYear + 1;
      let assets = lastHist.assets || 0;
      let debts = lastHist.debts || 0;
      let netWorth = lastHist.netWorth || 0;

      const debtProjections = debtsData.map((debt) => {
        const monthlyPayment = parseFloat(debt.actual_payment || 0);
        const annualPayment = monthlyPayment * 12;
        const balance = parseFloat(debt.remaining_balance || 0);
        const interestRate = 0.05;
        const monthlyRate = interestRate / 12;
        const yearsToPayoff =
          monthlyPayment > 0
            ? -Math.log(1 - (monthlyRate * balance) / monthlyPayment) /
              Math.log(1 + monthlyRate) /
              12
            : 99;
        const calculatedEndYear = Math.ceil(lastHistYear + yearsToPayoff);
        return {
          id: debt.debt_id,
          endYear: calculatedEndYear,
          monthlyPayment,
          annualPayment,
          currentBalance: balance,
          originalBalance: balance,
          monthlyRate,
          interestRate,
        };
      });

      for (let i = 1; i <= 25; i++) {
        startYear++;
        assets = assets * (1 + 0.045);
        let yearlyDebts = 0;
        debtProjections.forEach((debt) => {
          if (startYear <= debt.endYear) {
            const yearsFromStart = startYear - lastHistYear;
            const monthsFromStart = yearsFromStart * 12;
            const remainingBalance =
              debt.monthlyPayment > 0
                ? debt.originalBalance *
                    Math.pow(1 + debt.monthlyRate, monthsFromStart) -
                  (debt.monthlyPayment *
                    (Math.pow(1 + debt.monthlyRate, monthsFromStart) - 1)) /
                    debt.monthlyRate
                : debt.originalBalance;
            yearlyDebts += Math.max(0, remainingBalance);
          }
        });
        debts = yearlyDebts;
        netWorth = assets - debts;
        projectedData.push({
          date: `${startYear}`,
          assets,
          debts,
          netWorth,
          isProjection: true,
        });
      }
    }
    return projectedData;
  }, [lastHist, lastHistYear, debtsData]);

  const newProjectionWithCI = useMemo(() => {
    return projected.map((d, index) => {
      const baseCI = 0.02;
      const ciGrowthPerYear = 0.01;
      const maxCI = 0.4;
      const yearsOut = index + 1;
      const ciWidth = Math.min(baseCI + ciGrowthPerYear * yearsOut, maxCI);
      const minPercent = 0.7;
      const assetsLow = Math.max(
        d.assets * (1 - ciWidth),
        d.assets * minPercent,
      );
      const assetsHigh = d.assets * (1 + ciWidth);
      return {
        date: d.date,
        assets: d.assets,
        assetsHigh,
        assetsLow,
        debts: d.debts,
        debtsHigh: d.debts * (1 + ciWidth),
        debtsLow: d.debts * (1 - ciWidth),
        netWorth: d.netWorth,
        netWorthHigh: d.netWorth * (1 + ciWidth),
        netWorthLow: d.netWorth * (1 - ciWidth),
        isProjection: true,
        ciWidth,
      };
    });
  }, [projected]);

  useEffect(() => {
    setProjectionWithCI(newProjectionWithCI);
  }, [annualNetWorthTrend, debtsData]);

  const chartAssumptions = {
    assetGrowth: 0.045,
    annualDebtPayment: 30000,
    volatility: 0.12,
  };
  const [showAssumptions, setShowAssumptions] = useState(false);

  const allYears = [
    ...projected.map((d) => d.date),
    ...projected.map((d) => d.date),
  ];
  const minYear = Math.min(...allYears);
  const maxYear = Math.max(...allYears);

  const memberMap = useMemo(() => {
    const map = new Map();
    members.forEach((member) => {
      const id = member.userId || member.user_id;
      if (id) {
        map.set(id.toString(), {
          name: `${member.firstName || member.first_name || ""} ${member.lastName || member.last_name || ""}`.trim(),
          isFormSubmitter: member.isFormSubmitter,
        });
      }
    });
    return map;
  }, [members]);

  return (
    <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow mt-6">
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading assets data...</p>
        </div>
      ) : (
        <>
          {/* Charts Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              Financial Overview
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Asset Distribution Pie Chart (Recharts) */}
              <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-lg dark:shadow-none border border-gray-200 dark:border-gray-700 w-full">
                <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                  Asset Distribution
                </h4>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={assetDistributionData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      labelLine={true}
                    >
                      {assetDistributionData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Net Worth Trend Chart */}
              <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-lg dark:shadow-none border border-gray-200 dark:border-gray-700 w-full">
                <BalanceSheetNetWorthTrend
                  projectionWithCI={projectionWithCI}
                  historicalAssets={historical.map((d) => ({
                    start_date: d.date,
                    value: d.assets,
                    key: `assets-hist-${d.date}`,
                  }))}
                  projectedAssets={projected.map((d) => ({
                    start_date: d.date,
                    value: d.assets,
                    key: `assets-proj-${d.date}`,
                  }))}
                  historicalDebts={historical.map((d) => ({
                    start_date: d.date,
                    value: d.debts,
                    key: `debts-hist-${d.date}`,
                  }))}
                  projectedDebts={projected.map((d) => ({
                    start_date: d.date,
                    value: d.debts,
                    key: `debts-proj-${d.date}`,
                  }))}
                  debtsProjectionWithCI={projectionWithCI.map((d) => ({
                    date: d.date,
                    debts: d.debts,
                    debtsHigh: d.debtsHigh,
                    debtsLow: d.debtsLow,
                  }))}
                  historicalNetWorth={historical.map((d) => ({
                    start_date: d.date,
                    value: d.netWorth,
                    key: `networth-hist-${d.date}`,
                  }))}
                  projectedNetWorth={projected.map((d) => ({
                    start_date: d.date,
                    value: d.netWorth,
                    key: `networth-proj-${d.date}`,
                  }))}
                  lastHistYear={lastHistYear}
                  onShowAssumptions={() => setShowAssumptions(true)}
                >
                  <ProjectionAssumptionsModal
                    open={showAssumptions}
                    onClose={() => setShowAssumptions(false)}
                    assumptions={chartAssumptions}
                  />
                </BalanceSheetNetWorthTrend>
              </div>
            </div>
          </div>

          {/* Condensed Summary Section */}
          <div className="mb-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Assets
                </label>
                <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(totalAssets)}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Liabilities
                </label>
                <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(totalLiabilities)}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                  Net Worth
                </label>
                <div className="mt-1 text-xl font-semibold text-blue-600">
                  {formatCurrency(netWorth)}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                  Debt Ratio
                </label>
                <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {formatPercentage(
                    totalAssets ? totalLiabilities / totalAssets : 0,
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content - Assets & Liabilities */}
          <div className="mt-8">
            <div className="mb-8">
              <div className="mb-4">
                <h3 className="text-lg font-semibold">Assets</h3>
              </div>
              <AssetAnalysisTable
                assetsData={preparedAssets}
                members={membersToUse}
                assetTypesData={assetTypesToUse}
                taxQualificationsData={taxQualificationsToUse}
                accountTypesData={accountTypesToUse}
                financialInstitutionsData={financialInstitutionsToUse}
                ownerTypesData={ownerTypesToUse || []}
                onSave={handleSaveAssets}
                config={{
                  title: "",
                  editable: true,
                  showRiskTolerance: true,
                  showLastUpdated: true,
                  showMember: true,
                }}
              />
            </div>

            <div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold">Liabilities</h3>
              </div>
              <DebtAnalysisTable
                debtsData={debtsData}
                members={membersToUse}
                onSave={handleSaveLiabilities}
                config={{
                  title: "",
                  editable: true,
                  showDates: true,
                  showActualPayment: true,
                  showDebtorName: true,
                  showPayoffDate: true,
                  showLastUpdated: true,
                  showMember: true,
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BalanceSheet;
